import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Paper, Typography, Button } from '@material-ui/core';

import { ArchivalUnitCopy, EadNode } from 'types';
import { ReadOnlyField } from 'components/read-only-field/read-only-field';
import { RegisterField } from 'modules/register/register-field';
import { handleOriginatorLink } from 'modules/register/reqister-utils';
import { StructMap } from 'components/struct-map/struct-map';
import { StructMapData } from 'components/struct-map/struct-map-types';
import { LinkField } from 'components/link-field/link-field';
import { isDevelopment } from 'utils';
import { singleFetch } from 'utils/fetch';
import { ApiRoute } from 'enums';

import { PrivateArchivalUnitProvider } from './archival-unit-private-context';
import { ImageViewer } from 'components/image-viewer/image-viewer';
import { useStructMapImages } from 'components/struct-map/stuct-map-images-hook';
import { StructMapImageInfo } from 'components/struct-map/struct-map-image-info';
import { OpenCurrentUrlInBadatelna } from 'utils/dev-open-in-badatelna';
import { EadDetail } from 'modules/archival-aid/ead-detail/EadDetail';
import { findValidDescribingAid } from 'components/grid/v2/context/hierarchy-context';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    archivalUnitInfo: {
      marginTop: 10,
      fontWeight: 400,
      color: theme.palette.primary.main,
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
  })
);

export function ArchivalUnitDetail({
  item,
}: {
  item?: ArchivalUnitCopy & { structMap?: StructMapData };
}) {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    numericId,
    archivalUnitReference,
    summary,
    timeRange,
    evidenceTypeName,
    structMap,
    originators,
    zipFile,
    aipData = [],
  } = item || {};

  const {
    archivalCollection,
    entityCharacter,
    accessType,
    structuredLink,
    note,
    storageUnits = [],
    describingArchivalAids = [],
    publicCopy,
  } = archivalUnitReference || {};

  const { eadNode: eadNodeRef } = publicCopy || {};

  const archivalAid = findValidDescribingAid(describingArchivalAids);

  const downloadZippedContent = useCallback(async () => {
    window.open(
      process.env.PUBLIC_URL + `/api/file/archival/${zipFile?.id}`,
      '_blank'
    );
  }, [zipFile]);
  const [ead, setEad] = useState<EadNode | undefined>();

  useEffect(() => {
    const load = async (eadId: string) => {
      const eadNode = await singleFetch<EadNode>(
        `/api/${ApiRoute.ARCHIVAL_AID}/ead/${eadId}`,
        `ead-detail-${eadId}`
      );
      setEad(eadNode);
    };

    if (eadNodeRef?.id) {
      load(eadNodeRef.id);
    }
  }, [eadNodeRef?.id]);

  const {
    onLeafClick,
    imageViewerData,
    defaultIndex,
    toolbarButtons,
  } = useStructMapImages(structMap, item);

  if (ead) {
    return (
      <PrivateArchivalUnitProvider privateArchivalUnit={item}>
        <EadDetail item={ead} />
        {structMap !== undefined && imageViewerData.length > 0 && (
          <Paper elevation={0} className={classes.paper}>
            <ImageViewer
              defaultIndex={defaultIndex}
              allData={imageViewerData}
              toolbarButtons={toolbarButtons}
              InfoComponent={StructMapImageInfo}
              customProps={{
                parentEntity: item,
              }}
            />
          </Paper>
        )}
        {structMap !== undefined && (
          <Paper elevation={0} className={classes.paper} id="digital-content">
            <Grid container className={classes.root}>
              <div className={classes.content}>
                <Typography variant="h6">
                  {formatMessage({ id: 'archivalUnit.content' })}
                </Typography>
                {zipFile && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={downloadZippedContent}
                  >
                    {formatMessage({
                      id: 'archivalUnit.downloadAllContent',
                    })}
                  </Button>
                )}
              </div>

              <StructMap
                data={structMap}
                onChange={() => {}}
                onLeafClick={onLeafClick}
                parentEntity={item!}
              />
            </Grid>
          </Paper>
        )}
      </PrivateArchivalUnitProvider>
    );
  }

  return (
    <>
      {isDevelopment() && (
        <>
          DEV: AU PRIVATE DETAIL <OpenCurrentUrlInBadatelna />
        </>
      )}
      <Typography variant="h5">{summary}</Typography>
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <Typography variant="h6">
            {formatMessage({
              id: 'archivalUnit.inclusion',
            })}
          </Typography>
          <br />
          <br />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollection',
            })}
            value={archivalCollection?.name}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollectionNumber',
            })}
            value={archivalCollection?.evidenceNumber}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          {archivalAid && (
            <LinkField
              label={formatMessage({
                id: 'archivalUnit.archivalAid',
              })}
              value={archivalAid?.name}
              href={`pomucky/0/${archivalAid?.id}`}
            />
          )}
        </Grid>
      </Paper>

      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit',
            })}
            value={numericId?.toString()}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.regest',
            })}
            value={summary}
            alwaysShow={true}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.referenceDesignation',
            })}
            value={structuredLink}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.type',
            })}
            value={evidenceTypeName}
          />
          <ReadOnlyField
            label={formatMessage({ id: 'archivalUnit.form' })}
            value={formatMessage({
              id: `archivalUnit.entityCharacter.${entityCharacter}`,
            })}
          />
          <ReadOnlyField
            label={formatMessage({ id: 'archivalUnit.digitalization' })}
            Component={
              <AccessibleDigitalContent
                hasDigitalContent={aipData.length > 0}
                structMapData={structMap}
              />
            }
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.dating',
            })}
            value={timeRange}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.storageUnit',
            })}
            value={
              storageUnits.length > 0
                ? storageUnits.map((s) => s).join(', ')
                : undefined
            }
          />
          {accessType && (
            <ReadOnlyField
              label={formatMessage({
                id: 'archivalUnit.accessibility',
              })}
              value={formatMessage({
                id: `archivalUnit.accessType.${accessType}`,
                defaultMessage: `${accessType}`,
              })}
            />
          )}
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.note',
            })}
            value={note}
          />
        </Grid>
      </Paper>

      {originators?.map?.((o, i) => (
        <Grid item xs={12} key={o?.id}>
          <RegisterField
            key={o?.id}
            label={
              i === 0
                ? formatMessage({
                    id: 'archivalUnit.originator',
                  })
                : ''
            }
            withoutBorder={true}
            value={o?.label}
            valueLink={o && handleOriginatorLink(o, true)}
          />
        </Grid>
      ))}

      {structMap !== undefined && imageViewerData.length > 0 && (
        <Paper elevation={0} className={classes.paper}>
          <ImageViewer
            defaultIndex={defaultIndex}
            allData={imageViewerData}
            toolbarButtons={toolbarButtons}
            InfoComponent={StructMapImageInfo}
            customProps={{
              parentEntity: item,
            }}
          />
        </Paper>
      )}

      {structMap !== undefined && (
        <Paper elevation={0} className={classes.paper} id="digital-content">
          <Grid container className={classes.root}>
            <div className={classes.content}>
              <Typography variant="h6">
                {formatMessage({ id: 'archivalUnit.content' })}
              </Typography>
              {zipFile && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={downloadZippedContent}
                >
                  {formatMessage({
                    id: 'archivalUnit.downloadAllContent',
                  })}
                </Button>
              )}
            </div>

            <StructMap
              data={structMap}
              onChange={() => {}}
              parentEntity={item!}
              onLeafClick={onLeafClick}
            />
          </Grid>
        </Paper>
      )}
    </>
  );
}

export function AccessibleDigitalContent({
  hasDigitalContent,
  structMapData,
}: {
  hasDigitalContent: boolean;
  structMapData?: StructMapData;
}) {
  const { formatMessage } = useIntl();

  if (hasDigitalContent) {
    if (structMapData) {
      return (
        <a href="#digital-content" style={{ color: '#cf2931' }}>
          {formatMessage({ id: 'true' })}
        </a>
      );
    } else {
      return <span>{formatMessage({ id: 'true' })}</span>;
    }
  }

  return (
    <span>
      {formatMessage({
        id: 'false',
      })}
    </span>
  );
}
